<template>
  <section class="user-menu" v-if="authUser">
    <DropdownView position="is-top-right" class="is-full-width m-l-2 m-b-8">
      <template #trigger="{ active }">
        <section
          class="is-flex is-align-items-center is-justify-content-space-between is-full-width"
          :data-e2e="NavCypressAttribute.buttons.USER_MENU_DROPDOWN"
        >
          <div class="is-flex is-align-items-center">
            <UserAvatar
              :user="authUser"
              size="32"
              :wrap-avatar="authUser.permission.canAccessAdvisorPro"
            >
              <template v-if="authUser.permission.canAccessAdvisorPro" #badge>
                <img class="badge" src="@/assets/illustrations/nav/advisor-pro-badge.svg" />
              </template>
            </UserAvatar>
            <div class="is-flex is-flex-direction-column is-align-items-left ml-1">
              <p class="title mb-0 is-size-5 is-hidden-touch">
                {{ userName }}
              </p>
              <div class="is-flex is-flex-direction-row is-align-items-center">
                <p class="subtitle mb-0 is-size-6 is-hidden-touch">{{ subtitle }}</p>
              </div>
              <p class="is-hidden-tablet is-size-5 has-text-white">
                {{ $t('core.settings') }}
              </p>
            </div>
          </div>
          <i class="cls cls-caret-right is-size-5 mr-2" :class="{ expanded: active }"></i>
        </section>
      </template>
      <DropdownItemView custom>
        <div class="is-flex is-justify-content-space-between m-b-5">
          <section>
            <p class="title is-size-5 has-text-weight-semibold">{{ userName }}</p>
            <p class="subtitle is-size-6">{{ $t(roleName) }}</p>
          </section>
        </div>
        <section v-if="appVersion && authUser.isInternal" class="is-align-self-flex-end m-r-15">
          <TooltipView type="is-dark" :label="appVersion" :multilined="true" position="is-right">
            <p
              class="subtitle m-b-1 is-size-7 is-clickable"
              @click="onAppVersionClicked"
              :data-e2e="NavCypressAttribute.texts.APP_VERSION"
            >
              {{ $t('core.version') }}: {{ appVersion.slice(0, 8) }}
            </p>
          </TooltipView>
        </section>
      </DropdownItemView>
      <hr class="dropdown-divider" />
      <template v-if="authUser.permission.canChangeEstate && this.currentEstate">
        <DropdownItemView
          @click="onChangeEstateClicked"
          :data-e2e="NavCypressAttribute.buttons.CHANGE_ESTATE"
        >
          <i class="icon cls cls-move-left-right xy-l--4"></i>
          {{ $t('core.change-estate') }}
        </DropdownItemView>
      </template>
      <TimeEntryDropdownItem />
      <SpotlightSearchDropdownItem />
      <RichTextEditorToolDropdownItem />
      <hr
        class="dropdown-divider"
        v-if="
          (authUser.permission.canAccessSpotlightSearch || authUser.permission.canLogTime) &&
          !this.currentEstate
        "
      />
      <DropdownItem
        :link-to="{ name: AuthUserRoute.PROFILE.name }"
        icon="single-neutral-actions"
        icon-classes="xy-l-3 has-text-weight-semibold"
        :label="$t('auth.user-profile')"
      />
      <DropdownItem
        v-if="canAccessSettings"
        :link-to="{ name: EstateRoute.SETTINGS.name }"
        icon="cog"
        icon-classes="has-text-weight-semibold"
        :label="$t('core.estate-settings')"
        :data-e2e="NavCypressAttribute.buttons.ESTATE_SETTINGS"
      />
      <BillingDropdownItem v-if="canAccessBilling" />
      <LogIssueDropdownItem />
      <NovaDropdownItem
        v-if="!authUser.hasCurrentEstate"
        :data-e2e="NavCypressAttribute.buttons.CLEAR_DESK"
      />
      <DropdownItemView
        v-if="canRefer"
        @click="onReferFromUserMenu"
        :data-e2e="NavCypressAttribute.buttons.REFER_LOVED_ONE"
      >
        <i class="icon cls cls-gift-box"></i>
        {{ $t('Refer a loved one') }}
      </DropdownItemView>
      <hr class="dropdown-divider" />
      <div v-if="$slots.devButtons">
        <slot name="devButtons"></slot>
        <hr class="dropdown-divider" />
      </div>
      <DropdownItem
        v-if="isImpersonating"
        icon="logout-reverse"
        icon-classes="is-relative xy-t-2 has-text-weight-semibold"
        :label="$t('core.log-back-yourself')"
        :click-fn="onUnimpersonateClicked"
      />
      <DropdownItem
        v-else
        icon="logout-reverse"
        icon-classes="is-relative xy-t-2 has-text-weight-semibold"
        :label="$t('core.logout')"
        :click-fn="logoutUser"
        :data-e2e="NavCypressAttribute.buttons.LOGOUT"
      />
    </DropdownView>
  </section>
</template>

<script>
import { HasPermissionMixin } from '@/modules/core/app/mixins/HasPermissionMixin';
import { Env } from '/@/Env';
import AuthService from '/@/modules/auth/services/AuthService';
import UserAvatar from '/@/modules/user/components/presentational/UserAvatar.vue';
import { HasAuthUserMixin } from '/@/modules/core/app/mixins/HasAuthUserMixin';
import { HasCurrentEstateMixin } from '/@/modules/core/app/mixins/HasCurrentEstateMixin';
import ModalService from '/@/modules/core/ui/services/ModalService';
import { AuthUserRoute } from '/@/modules/auth/routes/auth.routes';
import EventBus from '/@/modules/core/app/helpers/EventBus';
import { NavEvents } from '/@/modules/core/nav/constants/nav.constants';
import { NavCypressAttribute } from '/@/modules/core/nav/constants/NavCypressAttribute';
import EstateSelectionModal from '/@/modules/estate/components/EstateSelectionModal.vue';
import Logger from '/@/modules/core/app/helpers/Logger';
import { EstateRoute } from '/@/modules/estate/constants/EstateRoute';
import LogIssueDropdownItem from '/@/modules/core/nav/components/dropdownitems/LogIssueDropdownItem.vue';
import NovaDropdownItem from '/@/modules/core/nav/components/dropdownitems/NovaDropdownItem.vue';
import { commits } from '/@/modules/core/app/helpers/store';
import { AuthUserLogOutMixin } from '/@/modules/auth/mixins/AuthUserLogOutMixin';
import DropdownItem from '/@/modules/core/app/components/DropdownItem.vue';
import BillingDropdownItem from '/@/modules/core/nav/components/dropdownitems/BillingDropdownItem.vue';
import TimeEntryDropdownItem from '/@/modules/core/nav/components/dropdownitems/TimeEntryDropdownItem.vue';
import { ShowReferralModalMixin } from '/@/modules/platformreferral/mixins/ShowReferralModalMixin';
import { CanReferMixin } from '/@/modules/platformreferral/mixins/CanReferMixin';
import SpotlightSearchDropdownItem from '@/modules/core/nav/components/dropdownitems/SpotlightSearchDropdownItem.vue';
import RichTextEditorToolDropdownItem from '@/modules/core/nav/components/dropdownitems/RichTextEditorToolDropdownItem.vue';
import DropdownItemView from '@/modules/core/ui/components/wrappers/DropdownItemView.vue';
import DropdownView from '@/modules/core/ui/components/wrappers/DropdownView.vue';
import { state } from '/@/modules/core/app/helpers/store';
import DeviceService from '@/modules/core/ui/services/DeviceService';
import ToastService from '@/modules/core/ui/services/ToastService';
import TooltipView from '@/modules/core/ui/components/wrappers/TooltipView.vue';

export default {
  compatConfig: {
    COMPONENT_V_MODEL: true
  },
  name: 'UserMenu',
  slotsDefinition: {
    devButtons: {}
  },
  mixins: [
    HasAuthUserMixin,
    HasCurrentEstateMixin,
    AuthUserLogOutMixin,
    HasPermissionMixin,
    CanReferMixin,
    ShowReferralModalMixin
  ],
  components: {
    SpotlightSearchDropdownItem,
    TimeEntryDropdownItem,
    DropdownItem,
    BillingDropdownItem,
    LogIssueDropdownItem,
    UserAvatar,
    NovaDropdownItem,
    RichTextEditorToolDropdownItem,
    DropdownItemView,
    DropdownView,
    TooltipView
  },
  static: {
    AuthUserRoute,
    EstateRoute,
    NavCypressAttribute
  },
  computed: {
    isExitingEstate() {
      return state.ui.isExitingEstate;
    },
    canAccessBilling() {
      return this.isSelfServe
        ? this.authUser.permission.canAccessClearWillBilling
        : this.authUser.permission.canAccessBilling;
    },
    subtitle() {
      return this.currentEstate ? this.customSubtitle : this.roleName;
    },
    customSubtitle() {
      return this.$t(`core.estatename-estatedisplaytype`, {
        estateName: this.currentEstate.contact?.fullName,
        estateDisplayType: this.currentEstate.displayType
      });
    },
    roleName() {
      if (this.authUser?.professional) {
        return this.authUser.professional?.professionValue;
      }
      return this.currentEstate ? this.authUser?.estateRoleName : this.authUser?.roleName;
    },
    userName() {
      return this.authUser?.name || '';
    },
    isImpersonating() {
      return this.authUser?.isImpersonated;
    },
    canAccessSettings() {
      return this.authUser.permission.canAccessEstateSettings && this.currentEstate?.isClearEstate;
    },
    appVersion() {
      return Env.getAppVersion();
    }
  },
  methods: {
    logoutUser() {
      this.logout();
    },
    async onUnimpersonateClicked() {
      commits.ui.setIsExitingEstate(true);
      try {
        await AuthService.unimpersonateUser(async (user) => {
          if (this.$route.name !== user.homePage) {
            await this.$router.push({ name: user.homePage });
          }
          EventBus.signal(NavEvents.RELOAD);
        });
      } catch (e) {
        Logger.error('Error while trying to log yourself back');
      } finally {
        commits.ui.setIsExitingEstate(false);
      }
    },
    onChangeEstateClicked() {
      ModalService.show({
        parent: this,
        component: EstateSelectionModal,
        width: '50%'
      });
    },
    onAppVersionClicked() {
      DeviceService.copyToClipboard(this.appVersion);
      ToastService.show(this.$t('core.version-copied'));
    }
  }
};
</script>

<style scoped>
section.user-menu {
  padding-top: var(--ce-7px);
  margin-right: var(--ce-7px);
  padding-left: var(--ce-10px);
  border-radius: 0 10px 10px 0;
  border: 2px solid transparent;
  cursor: pointer;
  position: relative;

  &:hover {
    font-weight: var(--ce-weight-medium);
    color: var(--ce-primary);
    border-color: var(--ce-primary);
    border-left-color: transparent;
  }

  p.text {
    font-size: var(--ce-14px);
    margin-left: var(--ce-5px);
  }

  .cls-caret-right {
    color: var(--ce-primary);
    font-size: var(--ce-12px);
    transition: transform 0.3s ease;

    &.expanded {
      transform: rotate(-90deg);
    }
  }
}

section.user-menu :deep(.dropdown-trigger) {
  width: 100%;
}

section.user-menu :deep(.dropdown-trigger .dropdown-menu) {
  margin-left: var(--ce-10px);
  margin-bottom: var(--ce-7px);
}

section.user-menu :deep(.dropdown-trigger .dropdown-menu .dropdown-item-label) {
  white-space: normal;
}

:deep(.avatar-wrapper) {
  background: linear-gradient(to top right, #425df6, #ff865b);
}
</style>
